import { useId } from 'react'
import type { InputHTMLAttributes, ReactNode, SelectHTMLAttributes, TextareaHTMLAttributes } from 'react'
import styles from './Field.module.css'

interface FieldShellProps {
  readonly id: string
  readonly label: string
  /** Hides the label visually while keeping it as the control's accessible
   *  name. For controls whose meaning is carried by a table column header —
   *  where a visible label on every row would be noise — never as a shortcut
   *  to skip labelling. */
  readonly hideLabel?: boolean
  readonly required?: boolean
  readonly description?: string | undefined
  readonly error?: string | undefined
  readonly descriptionId: string
  readonly errorId: string
  readonly children: ReactNode
}

/**
 * The wiring that makes a control accessible is identical for every control
 * type and easy to get subtly wrong, so it lives in exactly one place:
 * label→control association, `aria-describedby` pointing at the description
 * AND the error, `aria-invalid`, and an error that is announced when it
 * appears (`role="alert"`) rather than sitting silently on screen.
 */
function FieldShell({
  id, label, hideLabel, required, description, error, descriptionId, errorId, children,
}: FieldShellProps) {
  return (
    <div className={styles.field}>
      <label className={hideLabel ? 'sr-only' : styles.label} htmlFor={id}>
        {label}
        {required && (
          <span className={styles.required} aria-hidden="true">
            *
          </span>
        )}
      </label>
      {children}
      {description && (
        <span className={styles.description} id={descriptionId}>
          {description}
        </span>
      )}
      {error && (
        <span className={styles.error} id={errorId} role="alert">
          <span aria-hidden="true">▲</span>
          {error}
        </span>
      )}
    </div>
  )
}

/** Joins only the ids that actually exist — an aria-describedby pointing at
 *  a missing element is worse than none, since some screen readers skip the
 *  whole attribute rather than the dangling reference. */
const describedBy = (description: string | undefined, error: string | undefined, dId: string, eId: string) =>
  [description ? dId : null, error ? eId : null].filter(Boolean).join(' ') || undefined

export interface TextFieldProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'className' | 'id' | 'required'> {
  readonly label: string
  readonly description?: string
  /** `| undefined` is not noise: under `exactOptionalPropertyTypes` a bare
   *  `error?: string` refuses `error={maybeUndefined}`, which is how every
   *  caller computes one. FieldShell above already declares it this way. */
  readonly error?: string | undefined
  readonly required?: boolean
}

export function TextField({ label, description, error, required = false, ...rest }: TextFieldProps) {
  const id = useId()
  const dId = `${id}-description`
  const eId = `${id}-error`
  return (
    <FieldShell
      id={id} label={label} required={required} description={description}
      error={error} descriptionId={dId} errorId={eId}
    >
      <input
        {...rest}
        id={id}
        required={required}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(description, error, dId, eId)}
        className={[styles.control, error ? styles.invalid : ''].filter(Boolean).join(' ')}
      />
    </FieldShell>
  )
}

export interface TextAreaFieldProps
  extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'className' | 'id' | 'required'> {
  readonly label: string
  readonly description?: string
  /** See TextFieldProps.error for why `| undefined` is spelled out. */
  readonly error?: string | undefined
  readonly required?: boolean
}

export function TextAreaField({ label, description, error, required = false, ...rest }: TextAreaFieldProps) {
  const id = useId()
  const dId = `${id}-description`
  const eId = `${id}-error`
  return (
    <FieldShell
      id={id} label={label} required={required} description={description}
      error={error} descriptionId={dId} errorId={eId}
    >
      <textarea
        {...rest}
        id={id}
        required={required}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(description, error, dId, eId)}
        className={[styles.control, styles.textarea, error ? styles.invalid : ''].filter(Boolean).join(' ')}
      />
    </FieldShell>
  )
}

export interface SelectFieldProps
  extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'className' | 'id' | 'required'> {
  readonly label: string
  readonly hideLabel?: boolean
  readonly description?: string
  /** See TextFieldProps.error for why `| undefined` is spelled out. */
  readonly error?: string | undefined
  readonly required?: boolean
  readonly children: ReactNode
}

export function SelectField({
  label, hideLabel = false, description, error, required = false, children, ...rest
}: SelectFieldProps) {
  const id = useId()
  const dId = `${id}-description`
  const eId = `${id}-error`
  return (
    <FieldShell
      id={id} label={label} hideLabel={hideLabel} required={required} description={description}
      error={error} descriptionId={dId} errorId={eId}
    >
      <span className={styles.selectWrap}>
        <select
          {...rest}
          id={id}
          required={required}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy(description, error, dId, eId)}
          className={[styles.control, styles.select, error ? styles.invalid : ''].filter(Boolean).join(' ')}
        >
          {children}
        </select>
        <span className={styles.chevron} aria-hidden="true">
          ▼
        </span>
      </span>
    </FieldShell>
  )
}
